import React, { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';

/**
 * Local Icons to ensure sandbox compatibility and high-end feel
 */
const PortalIcons = {
    Check: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>,
    Clock: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    MapPin: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    Plus: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>,
    Notes: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
};

export default function PlannerProPublicView({ projectId, db, previewSettings }) {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('Overview');

    // Notes Interaction State
    const [newNoteTitle, setNewNoteTitle] = useState('');
    const [newNoteContent, setNewNoteContent] = useState('');
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [isSubmittingNote, setIsSubmittingNote] = useState(false);

    // Identity state for client actions
    const [clientName, setClientName] = useState(() => localStorage.getItem(`hostit_clientName_${projectId}`) || '');
    const [showNamePrompt, setShowNamePrompt] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    // Fetch Project Data
    useEffect(() => {
        if (!projectId || !db) {
            setError("Invalid Project ID");
            setLoading(false);
            return;
        }

        const projectRef = doc(db, 'plannerProjects', projectId);
        const unsubscribe = onSnapshot(projectRef, (docSnap) => {
            if (docSnap.exists()) {
                setProject({ id: docSnap.id, ...docSnap.data() });
                setError('');
            } else {
                setError("Project not found or no longer available.");
            }
            setLoading(false);
        }, (err) => {
            console.error("Error fetching shared project:", err);
            setError("Failed to load project details.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [projectId, db]);

    const requireClientName = (action) => {
        if (!clientName) {
            setPendingAction(() => action);
            setShowNamePrompt(true);
            return false;
        }
        return true;
    };

    const handleNameSubmit = (e) => {
        e.preventDefault();
        const name = new FormData(e.target).get('guestName');
        if (name && name.trim()) {
            const trimmedName = name.trim();
            localStorage.setItem(`hostit_clientName_${projectId}`, trimmedName);
            setClientName(trimmedName);
            setShowNamePrompt(false);
            if (pendingAction) pendingAction();
            setPendingAction(null);
        }
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayH = h % 12 || 12;
        return `${displayH}:${minutes} ${ampm}`;
    };

    const defaultSettings = {
        showOverview: true,
        showOverviewCountdown: true,
        showOverviewLocation: true,
        showTimeline: true,
        showTimelineNotes: false,
        showTimelineAssignees: false,
        showDesign: true,
        showDesignImages: true,
        showDesignColors: true,
        showVendors: false,
        showVendorContact: false,
        showBudget: false,
        showBudgetEstimated: true,
        showBudgetActuals: false,
        showTravel: true, 
        showCatering: true, 
        showChecklist: false,
        showGuestSummary: false,
        showNotes: false,
        allowChecklistEdits: false,
        allowNotesEdits: false
    };
    
    const shareSettings = useMemo(() => 
        previewSettings || { ...defaultSettings, ...(project?.shareSettings || {}) },
    [previewSettings, project?.shareSettings]);

    const tabs = useMemo(() => {
        const t = [{ id: 'Overview', label: 'Overview', show: shareSettings.showOverview }];
        t.push({ id: 'Timeline', label: 'Timeline', show: shareSettings.showTimeline });
        t.push({ id: 'Design', label: 'Design', show: shareSettings.showDesign });
        t.push({ id: 'Checklist', label: 'Checklist', show: shareSettings.showChecklist });
        t.push({ id: 'Catering', label: 'Catering', show: shareSettings.showCatering });
        t.push({ id: 'Travel', label: 'Travel', show: shareSettings.showTravel });
        t.push({ id: 'Notes', label: 'Notes', show: shareSettings.showNotes });
        t.push({ id: 'Budget', label: 'Budget', show: shareSettings.showBudget });
        return t.filter(x => x.show);
    }, [shareSettings]);

    const handleClientToggleTask = async (taskId) => {
        if (!shareSettings.allowChecklistEdits) return;
        if (!requireClientName(() => executeTaskToggle(taskId))) return;
        executeTaskToggle(taskId);
    };

    const executeTaskToggle = async (taskId) => {
        const updatedTasks = project.tasks.map(t => 
            t.id === taskId ? { ...t, completed: !t.completed, clientAction: true, lastUpdatedBy: clientName } : t
        );
        try {
            const projectRef = doc(db, 'plannerProjects', project.id);
            await updateDoc(projectRef, { tasks: updatedTasks });
        } catch (error) {
            console.error("Failed to update task:", error);
        }
    };

    const handleClientAddNote = async (e) => {
        if (e) e.preventDefault();
        if (!shareSettings.allowNotesEdits || !newNoteTitle.trim() || !newNoteContent.trim()) return;
        if (!requireClientName(() => executeAddNote())) return;
        executeAddNote();
    };

    const executeAddNote = async () => {
        setIsSubmittingNote(true);
        try {
            const noteObj = {
                id: Date.now().toString(),
                title: newNoteTitle.trim(),
                content: newNoteContent.trim(),
                date: new Date().toISOString(),
                author: clientName || 'Client',
                isClientNote: true
            };
            const updatedNotes = [noteObj, ...(project.notes || [])];
            const projectRef = doc(db, 'plannerProjects', project.id);
            await updateDoc(projectRef, { notes: updatedNotes });
            setNewNoteTitle('');
            setNewNoteContent('');
            setIsAddingNote(false);
        } catch (error) {
            console.error("Failed to add note:", error);
        } finally {
            setIsSubmittingNote(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-10">
            <div className="text-center">
                <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-500 font-medium">Loading Project Details...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-10">
            <div className="text-center bg-white p-8 rounded-2xl shadow-xl border border-red-100 max-w-sm">
                <div className="text-4xl mb-4">🚫</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Access Issue</h3>
                <p className="text-gray-500 text-sm">{error}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-24 font-sans selection:bg-pink-100 selection:text-pink-900 antialiased">
            {/* Identity Prompt Modal */}
            {showNamePrompt && (
                <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
                    <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-100 animate-fade-in-up">
                        <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-inner">👋</div>
                        <h3 className="text-2xl font-bold text-gray-900 text-center mb-2 font-serif">Welcome</h3>
                        <p className="text-gray-500 text-sm text-center mb-8 leading-relaxed">Please enter your name so your planner knows who is making changes.</p>
                        <form onSubmit={handleNameSubmit} className="space-y-4">
                            <input 
                                type="text" 
                                name="guestName" 
                                required 
                                placeholder="Your Full Name" 
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition-all font-bold text-center text-lg" 
                                autoFocus
                            />
                            <div className="flex gap-3">
                                <button type="button" onClick={() => { setShowNamePrompt(false); setPendingAction(null); }} className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition">Cancel</button>
                                <button type="submit" className="flex-1 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition shadow-lg">Continue</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Premium Hero Header */}
            <div className="h-80 md:h-[450px] relative overflow-hidden">
                <img src={project.coverImage || 'https://placehold.co/1200x600/f2a2e8/ffffff'} className="w-full h-full object-cover transition-transform duration-1000" alt="Cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
                <div className="absolute bottom-0 left-0 w-full p-6 md:p-12 flex flex-col md:flex-row justify-between items-end gap-6 z-10">
                    <div className="text-white max-w-3xl animate-fade-in-up">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] bg-white/20 px-3 py-1 rounded-full backdrop-blur-md border border-white/20">Client Access</span>
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] bg-pink-500 px-3 py-1 rounded-full shadow-lg">Live Portal</span>
                        </div>
                        <h1 className="text-4xl md:text-7xl font-serif font-bold drop-shadow-2xl tracking-tight leading-tight">{project.clientNames}</h1>
                        <p className="text-sm md:text-xl font-medium mt-4 drop-shadow flex flex-wrap items-center gap-2 opacity-90">
                            <span className="bg-black/30 backdrop-blur px-3 py-1 rounded-lg">📅 {project.date !== 'TBD' ? new Date(project.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Date TBD'}</span>
                            {shareSettings.showOverviewLocation !== false && (
                                <span className="bg-black/30 backdrop-blur px-3 py-1 rounded-lg">📍 {project.location}</span>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            {/* Interactive Navigation Pills */}
            <div className="sticky top-0 z-[50] bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm px-4 overflow-x-auto no-scrollbar py-3">
                <div className="container mx-auto flex items-center justify-start sm:justify-center gap-2 min-w-max">
                    {tabs.map(tab => (
                        <button 
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-6 py-2 rounded-full text-sm font-black uppercase tracking-widest transition-all duration-300 ${activeTab === tab.id ? 'bg-gray-900 text-white shadow-xl transform scale-105' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Section Renderer */}
            <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-12 mt-6 animate-fade-in">
                
                {/* 1. Overview */}
                {activeTab === 'Overview' && shareSettings.showOverview && (
                    <div className="space-y-8 animate-fade-in-up">
                        <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-pink-50 rounded-bl-full opacity-50 -z-10"></div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-10 font-serif text-gray-900">Event Overview</h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="bg-gray-50/80 p-8 rounded-3xl border border-gray-100 text-center flex flex-col justify-center">
                                    <span className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Countdown</span>
                                    {project.date !== 'TBD' ? (
                                        <div className="flex flex-col">
                                            <span className="text-6xl font-black text-pink-600 tracking-tighter">
                                                {Math.max(0, Math.ceil((new Date(project.date) - new Date()) / (1000 * 60 * 60 * 24)))}
                                            </span>
                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mt-2">Days to go</span>
                                        </div>
                                    ) : (
                                        <span className="text-2xl font-bold text-gray-400 italic">TBD</span>
                                    )}
                                </div>
                                <div className="md:col-span-2 space-y-8">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                        <div>
                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Date & Time</span>
                                            <p className="font-bold text-gray-900 text-xl">{project.date !== 'TBD' ? new Date(project.date).toLocaleDateString() : 'TBD'}</p>
                                            <p className="text-gray-500 text-sm">Full itinerary in Timeline tab.</p>
                                        </div>
                                        {shareSettings.showOverviewLocation && (
                                            <div>
                                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Primary Location</span>
                                                <p className="font-bold text-gray-900 text-xl">{project.location}</p>
                                                {project.venueName && (
                                                    <p className="text-sm text-pink-600 font-bold mt-1">📍 {project.venueName}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {shareSettings.showGuestSummary && (
                            <section className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
                                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-8 text-center">RSVP Progress Summary</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                    <div className="bg-emerald-50/50 p-6 rounded-3xl border border-emerald-100 text-center">
                                        <p className="text-4xl font-black text-emerald-600">{project.guests?.filter(g => g.rsvp === 'Attending').length || 0}</p>
                                        <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest mt-1">Attending</p>
                                    </div>
                                    <div className="bg-amber-50/50 p-6 rounded-3xl border border-amber-100 text-center">
                                        <p className="text-4xl font-black text-amber-600">{project.guests?.filter(g => g.rsvp === 'Pending').length || 0}</p>
                                        <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest mt-1">Pending</p>
                                    </div>
                                    <div className="bg-rose-50/50 p-6 rounded-3xl border border-rose-100 text-center">
                                        <p className="text-4xl font-black text-rose-600">{project.guests?.filter(g => g.rsvp === 'Declined').length || 0}</p>
                                        <p className="text-[10px] font-black text-rose-800 uppercase tracking-widest mt-1">Declined</p>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>
                )}

                {/* 2. Timeline */}
                {activeTab === 'Timeline' && shareSettings.showTimeline && (
                    <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold mb-12 font-serif text-gray-900 border-b border-gray-50 pb-6">Event Schedule</h2>
                        {project.timeline && project.timeline.length > 0 ? (
                            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-[115px] before:-translate-x-px before:h-full before:w-0.5 before:bg-gray-100 hidden md:block">
                                {[...project.timeline].sort((a,b) => (a.time || '').localeCompare(b.time || '')).map((item, idx) => (
                                    <div key={idx} className="relative flex items-start gap-12 group">
                                        <div className="flex flex-col items-end w-24 flex-shrink-0 pt-1">
                                            <span className="font-black text-pink-600 text-xl tracking-tight">{formatTime(item.time)}</span>
                                            {item.endTime && <span className="text-[10px] font-bold text-gray-400 uppercase mt-1">to {formatTime(item.endTime)}</span>}
                                        </div>
                                        <div className="absolute left-[108px] top-2.5 w-4 h-4 bg-white border-4 border-pink-500 rounded-full z-10 shadow-sm"></div>
                                        <div className="flex-1 bg-gray-50/80 p-6 rounded-3xl border border-gray-100 group-hover:bg-white group-hover:border-pink-200 group-hover:shadow-md transition-all">
                                            <div className="flex justify-between items-start gap-4 mb-2">
                                                <h4 className="font-bold text-gray-900 text-xl leading-tight">{item.event}</h4>
                                                <span className="text-[9px] font-black uppercase bg-white border border-gray-200 px-2 py-1 rounded-lg text-gray-400 shrink-0">{item.category}</span>
                                            </div>
                                            {(shareSettings.showTimelineNotes && item.notes) || (shareSettings.showTimelineAssignees && item.assigned) ? (
                                                <div className="mt-4 space-y-3">
                                                    {shareSettings.showTimelineNotes && item.notes && <p className="text-sm text-gray-600 italic leading-relaxed border-l-2 border-pink-200 pl-4">"{item.notes}"</p>}
                                                    {shareSettings.showTimelineAssignees && item.assigned && <p className="text-xs font-bold text-pink-600 flex items-center gap-2"><span className="w-5 h-5 bg-pink-100 rounded-full flex items-center justify-center text-[10px]">👤</span> {item.assigned}</p>}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 text-gray-400">
                                <div className="text-4xl mb-4 opacity-50"><PortalIcons.Clock /></div>
                                <p className="font-medium">Timeline is currently being finalized.</p>
                            </div>
                        )}
                        
                        {/* Mobile Timeline Version */}
                        <div className="md:hidden space-y-4">
                            {[...project.timeline].sort((a,b) => (a.time || '').localeCompare(b.time || '')).map((item, idx) => (
                                <div key={idx} className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="font-black text-pink-600 text-lg">{formatTime(item.time)}</span>
                                        <span className="text-[9px] font-black uppercase text-gray-400 tracking-widest">{item.category}</span>
                                    </div>
                                    <h4 className="font-bold text-gray-900 text-lg leading-tight mb-2">{item.event}</h4>
                                    {shareSettings.showTimelineNotes && item.notes && <p className="text-xs text-gray-500 italic mt-2">"{item.notes}"</p>}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* 3. Design Board */}
                {activeTab === 'Design' && shareSettings.showDesign && (
                    <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold mb-12 font-serif text-gray-900 border-b border-gray-50 pb-6">Aesthetic Concept</h2>
                        
                        {shareSettings.showDesignColors && project.colorPalettes?.length > 0 && (
                            <div className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-8">
                                {project.colorPalettes.map(palette => (
                                    <div key={palette.id} className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 shadow-inner">
                                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 ml-1">{palette.name}</h4>
                                        <div className="flex flex-wrap gap-3">
                                            {palette.colors.map((c, i) => (
                                                <div key={i} className="w-14 h-14 rounded-2xl border-4 border-white shadow-md hover:scale-110 transition-transform cursor-pointer" style={{ backgroundColor: c }} title={c}></div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {shareSettings.showDesignImages && (
                            <div className="columns-2 md:columns-3 lg:columns-4 gap-6 space-y-6">
                                {(project.designBoard || []).map(item => {
                                    if (item.type === 'image-group') {
                                        return (item.images || []).map((url, i) => (
                                            <div key={`${item.id}-${i}`} className="break-inside-avoid rounded-3xl overflow-hidden shadow-lg border border-gray-200 bg-gray-100 group cursor-zoom-in">
                                                <img src={url} alt="Inspo" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                            </div>
                                        ));
                                    }
                                    if (item.type === 'image') {
                                        return (
                                            <div key={item.id} className="break-inside-avoid rounded-3xl overflow-hidden shadow-lg border border-gray-200 bg-white flex flex-col group cursor-zoom-in">
                                                <img src={item.url} alt="Inspo" className="w-full h-auto object-cover block group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                                {item.caption && <div className="p-4 bg-white border-t border-gray-50 text-xs font-bold text-gray-700 uppercase tracking-wide">{item.caption}</div>}
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        )}
                        {(!project.designBoard || project.designBoard.length === 0) && (
                            <div className="text-center py-24 text-gray-400 border-2 border-dashed border-gray-200 rounded-[2.5rem] bg-gray-50">
                                <div className="text-5xl mb-4">🎨</div>
                                <p className="font-serif text-xl italic">The visual story for your event is being assembled.</p>
                            </div>
                        )}
                    </section>
                )}

                {/* 4. Checklist */}
                {activeTab === 'Checklist' && shareSettings.showChecklist && (
                    <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 animate-fade-in-up">
                        <div className="flex justify-between items-center mb-10 border-b border-gray-50 pb-6 flex-wrap gap-4">
                            <h2 className="text-3xl md:text-4xl font-bold font-serif text-gray-900">Planning Checklist</h2>
                            <div className="bg-emerald-50 text-emerald-700 px-5 py-2 rounded-full text-xs font-black uppercase tracking-[0.2em] border border-emerald-100">
                                {project.tasks?.filter(t => t.completed).length || 0} / {project.tasks?.length || 0} Completed
                            </div>
                        </div>
                        {project.tasks && project.tasks.length > 0 ? (
                            <div className="grid gap-3 max-w-4xl mx-auto">
                                {project.tasks.map(task => (
                                    <div key={task.id} className={`flex items-start gap-5 p-6 rounded-3xl border transition-all ${task.completed ? 'bg-gray-50/50 border-gray-100 opacity-60' : 'bg-white border-gray-100 shadow-sm hover:border-pink-300 hover:shadow-md'}`}>
                                        <div className="mt-1 flex-shrink-0">
                                            {shareSettings.allowChecklistEdits ? (
                                                <input 
                                                    type="checkbox" 
                                                    checked={task.completed || false} 
                                                    onChange={() => handleClientToggleTask(task.id)}
                                                    className="w-7 h-7 text-pink-600 rounded-xl border-gray-300 focus:ring-pink-500 cursor-pointer transition-transform active:scale-90"
                                                />
                                            ) : (
                                                <div className={`w-7 h-7 rounded-xl border-2 flex items-center justify-center ${task.completed ? 'bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-200' : 'border-gray-200 bg-gray-50'}`}>
                                                    {task.completed && <PortalIcons.Check />}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className={`font-bold text-lg md:text-xl leading-snug ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{task.text}</h4>
                                            <div className="flex flex-wrap gap-3 mt-3">
                                                <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{task.category || 'General'}</span>
                                                {task.dueDate && <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Target: {new Date(task.dueDate).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-24 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-[2.5rem] bg-gray-50">
                                <div className="text-5xl mb-4">📋</div>
                                <p className="font-serif text-xl italic">No tasks have been assigned yet.</p>
                            </div>
                        )}
                    </section>
                )}

                {/* 5. Catering / Menu */}
                {activeTab === 'Catering' && shareSettings.showCatering && (
                    <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold mb-12 font-serif text-gray-900 border-b border-gray-50 pb-6">Event Menu</h2>
                        {project.menuItems && project.menuItems.length > 0 ? (
                            <div className="space-y-16 max-w-4xl mx-auto">
                                {["Hors d'oeuvres", "Starter", "Main Course", "Sides", "Dessert", "Late Night Snack", "Beverages/Bar"].map(course => {
                                    const items = project.menuItems.filter(i => i.course === course);
                                    if (items.length === 0) return null;
                                    return (
                                        <div key={course} className="animate-fade-in-up">
                                            <div className="flex items-center gap-4 mb-8">
                                                <h3 className="text-2xl font-bold text-gray-900 font-serif whitespace-nowrap">{course}</h3>
                                                <div className="h-px w-full bg-pink-100"></div>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                {items.map(item => (
                                                    <div key={item.id} className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 hover:bg-white hover:border-pink-200 hover:shadow-md transition-all">
                                                        <h4 className="font-bold text-gray-900 text-lg mb-2">{item.name}</h4>
                                                        {item.description && <p className="text-sm text-gray-500 italic mb-4 leading-relaxed line-clamp-3">"{item.description}"</p>}
                                                        {item.tags && item.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-2 mt-auto pt-2">
                                                                {item.tags.map((tag, idx) => (
                                                                    <span key={idx} className="bg-emerald-50 text-emerald-700 border border-emerald-100 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-[0.1em]">
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="py-24 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-[2.5rem] bg-gray-50">
                                <div className="text-5xl mb-4">🍽️</div>
                                <p className="font-serif text-xl italic">The menu is being crafted by our catering team.</p>
                            </div>
                        )}
                    </section>
                )}

                {/* 6. Notes */}
                {activeTab === 'Notes' && shareSettings.showNotes && (
                    <div className="space-y-8 animate-fade-in-up">
                        <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100">
                            <div className="flex justify-between items-center mb-10 border-b border-gray-50 pb-6 flex-wrap gap-4">
                                <h2 className="text-3xl md:text-4xl font-bold font-serif text-gray-900">Project Notes</h2>
                                {shareSettings.allowNotesEdits && (
                                    <button 
                                        onClick={() => setIsAddingNote(!isAddingNote)}
                                        className={`px-6 py-2.5 rounded-full text-sm font-black uppercase tracking-widest transition-all ${isAddingNote ? 'bg-gray-100 text-gray-500' : 'bg-pink-600 text-white shadow-xl hover:bg-pink-700 transform hover:-translate-y-1'}`}
                                    >
                                        {isAddingNote ? 'Cancel' : '+ Add Note'}
                                    </button>
                                )}
                            </div>

                            {isAddingNote && (
                                <div className="max-w-2xl mx-auto mb-16 animate-fade-in-up">
                                    <form onSubmit={handleClientAddNote} className="bg-yellow-50/50 p-8 rounded-[2rem] border-2 border-yellow-200 shadow-xl space-y-6">
                                        <div>
                                            <label className="text-[10px] font-black text-yellow-800 uppercase tracking-widest block mb-2 ml-1">Subject</label>
                                            <input 
                                                type="text" 
                                                required 
                                                value={newNoteTitle} 
                                                onChange={e => setNewNoteTitle(e.target.value)}
                                                placeholder="e.g. Question about floral delivery..." 
                                                className="w-full p-4 bg-white border border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-400 outline-none transition font-bold"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-yellow-800 uppercase tracking-widest block mb-2 ml-1">Note Content</label>
                                            <textarea 
                                                required 
                                                value={newNoteContent} 
                                                onChange={e => setNewNoteContent(e.target.value)}
                                                placeholder="Write your note to the planner here..." 
                                                className="w-full p-4 bg-white border border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-400 outline-none transition h-40 resize-none font-medium"
                                            />
                                        </div>
                                        <button 
                                            type="submit" 
                                            disabled={isSubmittingNote}
                                            className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-black transition shadow-lg shadow-yellow-900/10 transform active:scale-95 flex items-center justify-center gap-2"
                                        >
                                            {isSubmittingNote ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save Note'}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {project.notes && project.notes.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {project.notes.map(note => (
                                        <div key={note.id} className={`p-8 rounded-[2rem] shadow-sm border transition-all hover:shadow-md ${note.isClientNote ? 'bg-blue-50/30 border-blue-100' : 'bg-yellow-50/50 border-yellow-100'}`}>
                                            <div className="flex justify-between items-start mb-4">
                                                <h4 className="font-bold text-gray-900 text-xl font-serif">{note.title}</h4>
                                                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${note.isClientNote ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                                    {note.isClientNote ? 'Client' : 'Planner'}
                                                </span>
                                            </div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">{note.date ? new Date(note.date).toLocaleDateString() : ''}</p>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed font-medium">{note.content}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-20 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-[2.5rem] bg-gray-50">
                                    <div className="text-5xl mb-4 grayscale opacity-40"><PortalIcons.Notes /></div>
                                    <p className="font-serif text-xl italic">No notes shared yet.</p>
                                </div>
                            )}
                        </section>
                    </div>
                )}
                
                {/* 7. Budget Overview */}
                {activeTab === 'Budget' && shareSettings.showBudget && (
                    <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 animate-fade-in-up">
                        <div className="flex justify-between items-center mb-10 border-b border-gray-50 pb-6 flex-wrap gap-4">
                            <h2 className="text-3xl md:text-4xl font-bold font-serif text-gray-900">Financial Summary</h2>
                            <span className="bg-red-50 text-red-700 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-red-100 shadow-sm">Confidential Access</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                            {shareSettings.showBudgetEstimated && (
                                <div className="bg-gray-900 text-white p-10 rounded-[2rem] shadow-2xl relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full group-hover:scale-110 transition-transform duration-700"></div>
                                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] mb-4 block">Planned Investment</span>
                                    <span className="text-5xl font-black tracking-tighter">${(project.budgetItems || []).reduce((a,b) => a + (Number(b.estimated) || 0), 0).toLocaleString()}</span>
                                </div>
                            )}
                            {shareSettings.showBudgetActuals && (
                                <div className="bg-emerald-600 text-white p-10 rounded-[2rem] shadow-2xl relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full group-hover:scale-110 transition-transform duration-700"></div>
                                    <span className="text-[10px] font-black text-emerald-200 uppercase tracking-[0.2em] mb-4 block">Current Spending</span>
                                    <span className="text-5xl font-black tracking-tighter">${(project.budgetItems || []).reduce((a,b) => a + (Number(b.actual) || 0), 0).toLocaleString()}</span>
                                </div>
                            )}
                        </div>

                        {shareSettings.showBudgetActuals && project.budgetItems?.length > 0 && (
                            <div className="mt-16 space-y-4 max-w-4xl mx-auto">
                                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6 ml-2">Expense Allocation Breakdown</h3>
                                <div className="grid gap-3">
                                    {project.budgetItems.map(item => (
                                        <div key={item.id} className="flex justify-between items-center p-5 bg-gray-50/50 rounded-2xl border border-gray-100 hover:bg-white hover:border-pink-200 transition-all">
                                            <div className="min-w-0 pr-4">
                                                <p className="font-bold text-gray-900 truncate">{item.name}</p>
                                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1 block">{item.category}</span>
                                            </div>
                                            <span className="font-black text-gray-900 text-lg">${(Number(item.actual) || 0).toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* 8. Travel */}
                {activeTab === 'Travel' && shareSettings.showTravel && (
                    <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-100 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold mb-12 font-serif text-gray-900 border-b border-gray-50 pb-6">Accommodations</h2>
                        {((project.roomBlocks && project.roomBlocks.length > 0) || (project.transportation && project.transportation.length > 0)) ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
                                {project.roomBlocks && project.roomBlocks.length > 0 && (
                                    <div className="space-y-6">
                                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2"><span>🏨</span> Hotel Blocks</h3>
                                        <div className="space-y-6">
                                            {project.roomBlocks.map(block => (
                                                <div key={block.id} className="bg-blue-50/30 p-8 rounded-[2rem] border border-blue-100 relative group">
                                                    <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100/50 rounded-bl-full -z-10"></div>
                                                    <h4 className="font-bold text-gray-900 text-2xl font-serif mb-4">{block.hotelName}</h4>
                                                    <div className="space-y-3 bg-white/60 backdrop-blur p-5 rounded-2xl border border-blue-50 shadow-inner mb-6">
                                                        {block.rate && <p className="flex justify-between items-center"><span className="text-[10px] font-black text-gray-400 uppercase">Rate</span> <span className="font-black text-gray-900">${block.rate}</span></p>}
                                                        {block.cutoffDate && <p className="flex justify-between items-center"><span className="text-[10px] font-black text-gray-400 uppercase">Book By</span> <span className="font-black text-rose-600">{new Date(block.cutoffDate).toLocaleDateString()}</span></p>}
                                                    </div>
                                                    {block.link && (
                                                        <a href={block.link} target="_blank" rel="noopener noreferrer" className="block w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-center shadow-lg hover:bg-blue-700 transition transform hover:-translate-y-1">
                                                            View Booking
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {project.transportation && project.transportation.length > 0 && (
                                    <div className="space-y-6">
                                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2"><span>🚌</span> Shuttles</h3>
                                        <div className="space-y-4">
                                            {project.transportation.sort((a,b) => a.time.localeCompare(b.time)).map(t => (
                                                <div key={t.id} className="flex gap-5 p-6 bg-gray-50 border border-gray-100 rounded-3xl items-center hover:bg-white hover:border-blue-200 transition-all shadow-sm">
                                                    <div className="bg-white text-gray-900 font-black px-4 py-2.5 rounded-2xl border border-gray-100 shadow-md font-mono text-sm shrink-0">{t.time}</div>
                                                    <div className="min-w-0">
                                                        <p className="font-bold text-gray-900 text-lg truncate leading-tight">{t.company}</p>
                                                        {t.route && <p className="text-xs text-blue-600 font-bold mt-1 tracking-tight truncate">{t.route}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-24 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-[2.5rem] bg-gray-50">
                                <div className="text-5xl mb-4 opacity-50"><PortalIcons.MapPin /></div>
                                <p className="font-serif text-xl italic">Travel details will be posted here as they are secured.</p>
                            </div>
                        )}
                    </section>
                )}
            </div>
            
            {/* Minimal Platform Branding */}
            <div className="text-center py-20 border-t border-gray-100 mt-20 bg-white">
                <div className="flex flex-col items-center justify-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl font-black text-pink-600 italic">Host-it!</span>
                        <span className="text-lg font-serif font-bold text-gray-800 tracking-tight">Pro Portal</span>
                    </div>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.3em]">Designing Unforgettable Memories</p>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{__html: `
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
                .animate-fade-in-up { animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}} />
        </div>
    );
}